import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import styled from 'styled-components';
import { ClinicLocationDetailsTabType } from '../../common/model/type/clinic-location-details-tab.type';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import DeleteIcon from '@mui/icons-material/Delete';
import ClinicLocationInfo from './ClinicLocationInfo';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import FilterableClinicLocationProvidersList from './FilterableClinicLocationProvidersList';
import FilterableClinicLocationPatientsList from './FilterableClinicLocationPatientsList';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import { useQuery } from '@tanstack/react-query';
import { ClinicLocationService } from '../../services/clinic-location.service';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useAdminClinicLocationMutations } from '../../hooks/use-admin-clinic-location-mutations';

const getActiveTab = (tabPath: string): ClinicLocationDetailsTabType => {
  const tab = tabPath.split('/')[0] as ClinicLocationDetailsTabType;

  return Object.values(ClinicLocationDetailsTabType).includes(tab)
    ? tab
    : ClinicLocationDetailsTabType.CLINIC_LOCATION_INFO;
};

const ClinicLocationDetails = () => {
  const { t } = useTranslation();

  const applicationOperations = useAuthorized(Resource.Application);
  const clinicLocationOperations = useAuthorized(Resource.ClinicLocation);

  const clinicLocationDeleteAllowed =
    clinicLocationOperations.delete || applicationOperations.supervise;
  const clinicLocationUpdateAllowed =
    clinicLocationOperations.update || applicationOperations.supervise;

  const {
    clinicId,
    clinicLocationId,
    '*': tabPath = '',
  } = useParams() as {
    clinicId: string;
    clinicLocationId: string;
    '*'?: string;
  };
  const url = useRouteUrl();
  const clinicInfoPath = `/clinics/${clinicId}/clinic-info`;
  const [isOpenDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const {
    data: clinicLocation,
    isFetching: isGetClinicLocationFetching,
    error: getClinicLocationError,
    refetch,
  } = useQuery({
    queryKey: ['admin-clinic-location', clinicLocationId],
    queryFn: () =>
      ClinicLocationService.getClinicLocationById(clinicLocationId),
    enabled: !!clinicLocationId,
  });

  const { deleteClinicLocation, updateClinicLocation } =
    useAdminClinicLocationMutations(clinicLocationId);

  const handleDeleteClinicLocation = () => {
    setOpenDeleteConfirmationDialog(false);
    deleteClinicLocation.mutate(undefined, {
      onSuccess: () => navigate(clinicInfoPath),
    });
  };

  if (!clinicLocation) {
    return (
      <OvRecordDetailsPage>
        {getClinicLocationError ? (
          <OvRecordDetailsErrorState
            title={t('clinicLocationDetails.details.errorTitle')}
            error={getClinicLocationError}
            onRetry={() => refetch()}
            backLabel={t('clinicDetails.tabs.clinicInfo')}
            backPath={clinicInfoPath}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  const mutationError =
    getClinicLocationError ??
    deleteClinicLocation.error ??
    updateClinicLocation.error;
  const isLoading =
    isGetClinicLocationFetching ||
    deleteClinicLocation.isPending ||
    updateClinicLocation.isPending;

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {mutationError && (
        <OvErrorMessage message={getAxiosErrorMessage(mutationError)} />
      )}

      {clinicLocation.is_demo_clinic_location && (
        <StyledAlert severity="warning">
          <AlertTitle>{t('clinicLocationDetails.demoAlertTitle')}</AlertTitle>
          {t('clinicLocationDetails.demoAlertDesc')}
        </StyledAlert>
      )}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('clinicLocationDetails.details.breadcrumb')}
          backControl={
            <OvBackButton
              label={t('clinicDetails.tabs.clinicInfo')}
              fallbackPath={clinicInfoPath}
            />
          }
          current={clinicLocation.name}
          title={clinicLocation.name}
          titleAside={
            clinicLocation.is_demo_clinic_location && (
              <OvStatusChip label={t('userList.demo')} tone="caution" />
            )
          }
          meta={
            <>
              <span>
                {clinicLocation.id}
                <OvCopyToClipboardButton
                  text={clinicLocation.id}
                  label={t('clinicLocationDetails.details.copyId')}
                />
              </span>
              {clinicLocation.city && <span>{clinicLocation.city}</span>}
              {clinicLocation.created_at && (
                <span>
                  <OvDateParser
                    date={clinicLocation.created_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            clinicLocationDeleteAllowed && (
              <OvRecordHeaderActions>
                <OvCompactButton
                  tone="danger"
                  icon={<DeleteIcon />}
                  onClick={() => setOpenDeleteConfirmationDialog(true)}
                >
                  {t('common.actions.delete')}
                </OvCompactButton>
              </OvRecordHeaderActions>
            )
          }
        />

        <TabContainer>
          <Tabs value={getActiveTab(tabPath)} variant="scrollable">
            <Tab
              value={ClinicLocationDetailsTabType.CLINIC_LOCATION_INFO}
              label={t('clinicLocationDetails.tabs.clinicSiteInfo')}
              component={Link}
              to={`${url}/${ClinicLocationDetailsTabType.CLINIC_LOCATION_INFO}`}
            />
            <Tab
              value={ClinicLocationDetailsTabType.PATIENTS}
              label={t('clinicLocationDetails.tabs.patients')}
              component={Link}
              to={`${url}/${ClinicLocationDetailsTabType.PATIENTS}`}
            />
            <Tab
              value={ClinicLocationDetailsTabType.PROVIDERS}
              label={t('clinicLocationDetails.tabs.providers')}
              component={Link}
              to={`${url}/${ClinicLocationDetailsTabType.PROVIDERS}`}
            />
          </Tabs>
        </TabContainer>

        <Routes>
          <Route
            index
            element={
              <Navigate
                to={`${url}/${ClinicLocationDetailsTabType.CLINIC_LOCATION_INFO}`}
                replace
              />
            }
          />
          <Route
            path="clinic-location-info/*"
            element={
              <OvRecordDetailsSection
                title={t('clinicLocationDetails.tabs.clinicSiteInfo')}
              >
                <ClinicLocationInfo
                  selectedClinicLocation={clinicLocation}
                  handleClinicLocationUpdate={updateClinicLocation.mutate}
                  canEdit={clinicLocationUpdateAllowed}
                />
              </OvRecordDetailsSection>
            }
          />
          <Route
            path="providers/*"
            element={
              <TabContent>
                <FilterableClinicLocationProvidersList
                  selectedClinicLocation={clinicLocation}
                  clinicId={clinicId}
                />
              </TabContent>
            }
          />
          <Route
            path="patients/*"
            element={
              <TabContent>
                <FilterableClinicLocationPatientsList
                  selectedClinicLocation={clinicLocation}
                  clinicId={clinicId}
                />
              </TabContent>
            }
          />
        </Routes>
      </OvRecordDetailsCanvas>

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isOpenDeleteConfirmationDialog}
        onCancel={() => setOpenDeleteConfirmationDialog(false)}
        onConfirm={handleDeleteClinicLocation}
        title="clinicLocationDetails.actions.deleteTitle"
        description="clinicLocationDetails.actions.deleteDescription"
      />

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </OvRecordDetailsPage>
  );
};

export default ClinicLocationDetails;

const TabContainer = styled.div`
  padding: 0 1rem;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  && {
    .MuiTab-root {
      text-transform: none;
    }

    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const TabContent = styled.div`
  padding: 1.25rem 1.5rem;
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const StyledAlert = styled(Alert)`
  && {
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
